import React, { useContext, useEffect, useState } from "react";
import { Button } from "@mui/material";
import BuildIcon from "@mui/icons-material/Build";

import Modal from "components/Modals/Modal";
import AppStateContext from "contexts/AppStateContext";
import useStateWithLocalStorage from "hooks/UseStateWithLocalStorage";

import styles from "./MachineMaintenanceReminder.module.css";

interface MaintenanceWindow {
  startHour: number;
  endHour: number;
}

// Daily windows (browser local time) where the reminder is active.
const WINDOWS: MaintenanceWindow[] = [
  { startHour: 6, endHour: 7 },
  { startHour: 13, endHour: 14 },
];

const SNOOZE_MS = 15 * 60 * 1000;
const TICK_MS = 30 * 1000;

const getActiveWindow = (now: Date): MaintenanceWindow | null =>
  WINDOWS.find(
    (win) => now.getHours() >= win.startHour && now.getHours() < win.endHour
  ) ?? null;

const windowKey = (now: Date, win: MaintenanceWindow): string => {
  const month = `${now.getMonth() + 1}`.padStart(2, "0");
  const day = `${now.getDate()}`.padStart(2, "0");
  return `${now.getFullYear()}-${month}-${day}-${win.startHour}`;
};

const MachineMaintenanceReminder = () => {
  const [acknowledgedWindow, setAcknowledgedWindow] = useStateWithLocalStorage<
    string | null
  >(null, "machineMaintenanceAck");
  const [snoozeUntil, setSnoozeUntil] = useStateWithLocalStorage<number | null>(
    null,
    "machineMaintenanceSnoozeUntil"
  );
  const [isOpen, setIsOpen] = useState(false);

  // Manually triggered from the settings page to preview the reminder outside
  // its windows. A manual preview never writes to localStorage, so it cannot
  // suppress a real reminder.
  const { showMaintenanceReminder, setShowMaintenanceReminder } =
    useContext(AppStateContext);

  useEffect(() => {
    if (showMaintenanceReminder) {
      setIsOpen(true);
      return;
    }

    const check = () => {
      const now = new Date();
      const win = getActiveWindow(now);

      if (!win) {
        setIsOpen(false);
        return;
      }
      if (acknowledgedWindow === windowKey(now, win)) return;
      if (snoozeUntil && now.getTime() < snoozeUntil) return;

      setIsOpen(true);
    };

    check();
    const intervalId = setInterval(check, TICK_MS);
    return () => clearInterval(intervalId);
  }, [acknowledgedWindow, snoozeUntil, showMaintenanceReminder]);

  const handleDone = () => {
    setIsOpen(false);
    if (showMaintenanceReminder) {
      setShowMaintenanceReminder(false);
      return;
    }

    const now = new Date();
    const win = getActiveWindow(now);
    if (win) setAcknowledgedWindow(windowKey(now, win));
    setSnoozeUntil(null);
  };

  const handleNotYet = () => {
    setIsOpen(false);
    if (showMaintenanceReminder) {
      setShowMaintenanceReminder(false);
      return;
    }

    setSnoozeUntil(Date.now() + SNOOZE_MS);
  };

  return (
    <Modal isOpen={isOpen} onClose={handleNotYet}>
      <div className={styles.card}>
        <BuildIcon className={styles.icon} sx={{ fontSize: 40 }} />
        <h2 className={styles.title}>
          Is it the time for the Machine Maintenance?
        </h2>
        <p className={styles.subtitle}>
          Confirm once the maintenance has been carried out.
        </p>
        <div className={styles.actions}>
          <Button variant="outlined" color="inherit" onClick={handleNotYet}>
            Not yet
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleDone}
            autoFocus
          >
            Maintenance is done
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default MachineMaintenanceReminder;
